import { sanitizePath } from "../../../utils/sanitizePath";

/**
 * Uploads an image to AWS S3.
 * @component uploadImageToS3
 * @author Sanjay
 *
 */

const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  "application/x-zip-compressed",
  "video/mp4",
  "video/webm",
  "video/avi",
  "video/mkv",
  "audio/mpeg",
  "text/csv",
  "text/plain",
];

const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "zip",
  "mp4",
  "webm",
  "avi",
  "mkv",
  "mp3",
  "csv",
  "txt",
];

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const validateFile = (file: any): void => {
  if (!file) {
    throw new Error("No file provided");
  }
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new Error(`File type "${file.type}" is not allowed`);
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File size exceeds the 50 MB limit");
  }
  const ext = file.name?.split(".")?.pop()?.toLowerCase();
  if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
    throw new Error(`File extension ".${ext}" is not allowed`);
  }
};

export const uploadImageToS3 = async ({
  file,
  userId,
  caseId,
  inmateName,
  folderName,
  request,
}: any) => {
  validateFile(file);

  const sanitizedInmate = sanitizePath(inmateName);
  const sanitizedCaseId = sanitizePath(caseId);
  const sanitizedFileName = sanitizePath(file.name) || "file";

  const payload: any = {
    fileName: sanitizedFileName,
    fileType: file.type,
    folderName,
    userId,
  };
  if (sanitizedCaseId) {
    payload.caseId = sanitizedCaseId;
    payload.inmateName = sanitizedInmate;
  }

  const presignedResponse = await request.post(
    "/api/aws/pre-signed-url",
    payload,
  );

  const { signedUrl, key } = presignedResponse.data;

  const uploadResponse = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.statusText}`);
  }

  return { Location: signedUrl.split("?")[0], Key: key };
};
